import React, { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { api } from '../../lib/api';
import './ScrollingBanner.css';

const priorityConfig = {
  critical: {
    icon: AlertTriangle,
    bg: 'bg-red-600',
    text: 'text-white'
  },
  warning: {
    icon: AlertCircle,
    bg: 'bg-amber-500',
    text: 'text-white'
  },
  info: {
    icon: Info,
    bg: 'bg-blue-600',
    text: 'text-white'
  }
};

const speedConfig = {
  slow: '40s',
  normal: '25s',
  fast: '15s'
};

export const ScrollingBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);

  useEffect(() => {
    fetchAnnouncements();
    // Refresh every 60 seconds to check for new/expired announcements
    const interval = setInterval(fetchAnnouncements, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.getActiveAnnouncements();
      setAnnouncements(response.announcements || []);
    } catch (error) {
      // Silent fail - don't break the app if announcements fail
      console.error('Failed to fetch announcements:', error);
    }
  };

  const handleDismiss = (id) => {
    // Only dismiss locally (for this session) - admins control actual deactivation
    setDismissedIds(prev => [...prev, id]);
  };

  // Filter out dismissed announcements
  const visibleAnnouncements = announcements.filter(
    ann => !dismissedIds.includes(ann.id)
  );

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="scrolling-banner-container">
      {visibleAnnouncements.map((announcement) => {
        const config = priorityConfig[announcement.priority] || priorityConfig.info;
        const Icon = config.icon;
        const speed = speedConfig[announcement.scroll_speed] || speedConfig.normal;
        const customBg = announcement.background_color || null;

        return (
          <div
            key={announcement.id}
            className={`scrolling-banner ${config.bg} ${config.text}`}
            style={customBg ? { backgroundColor: customBg } : undefined}
            data-testid={`announcement-${announcement.id}`}
          >
            <div className="banner-content">
              <div 
                className="scrolling-text"
                style={{ animationDuration: speed }}
              >
                <span className="banner-icon">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="banner-message">
                  {announcement.message}
                </span>
                {announcement.linked_incident_number && (
                  <span className="banner-incident">
                    (Related: {announcement.linked_incident_number})
                  </span>
                )}
                {/* Repeat for seamless loop */}
                <span className="banner-spacer">•••</span>
                <span className="banner-icon">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="banner-message">
                  {announcement.message}
                </span>
                {announcement.linked_incident_number && (
                  <span className="banner-incident">
                    (Related: {announcement.linked_incident_number})
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => handleDismiss(announcement.id)}
              className="banner-dismiss"
              title="Dismiss for this session"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ScrollingBanner;
